import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import type { PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Button } from "@/components/flexkit/Button";
import { CancelWorkflowExecutionActionDialog } from "./CancelAutomationExecutionActionDialog";
import { canCancelAction } from "@/utils/automation-execution-action";

export const WorkflowExecutionActionDropdownMenu: FC<{
    action: PlainWorkflowExecutionAction;
}> = ({ action }) => {
    const { toast } = useToast();
    const [showCancelModal, setShowCancelModal] = useState(false);

    const handleCopyId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(action.id);
            toast({
                title: "Action ID copied to clipboard",
                description: action.id,
            });
        } catch (error) {
            toast({
                title: "Failed to copy action ID",
                description: formatError(error),
            });
        }
    }, [toast, action.id]);

    const handleCancelClick = useCallback(() => setShowCancelModal(true), []);

    const canCancel = canCancelAction(action);

    return (
        <>
            <DropdownActions
                triggerTestId="workflow-execution-action-dropdown-trigger"
                button={<Button variant="outline" aria-label="More actions" LeadingIcon={IconEllipsisHorizontal} />}
            >
                <DropdownMenuItem
                    onClick={handleCopyId}
                    data-testid="workflow-execution-action-dropdown-copy-id"
                    data-tracking-id="copy-id-workflow-execution-action-dropdown"
                >
                    Copy ID
                </DropdownMenuItem>
                {canCancel && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            variant="destructive"
                            onClick={handleCancelClick}
                            data-testid="workflow-execution-action-dropdown-cancel"
                            data-tracking-id="cancel-workflow-execution-action-dropdown"
                        >
                            Cancel
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownActions>
            <CancelWorkflowExecutionActionDialog
                open={showCancelModal}
                action={action}
                onClose={() => setShowCancelModal(false)}
            />
        </>
    );
};
